import { Button, Icon, Text } from '@southfields-digital/mpxlive-components';
import classNames from 'classnames';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useMosActions } from 'src/components/Playlists/useMosActions';
import { StateType } from 'src/redux/reducers';
import { REQUEST_DELETE_PLAYLIST, REQUEST_DUPLICATE_PLAYLIST } from 'src/redux/reducers/playlist';

import type { PlaylistActionBarProps } from './PlaylistActionBar';

export default function MosActionBar({
  playlistId,
  editable,
  onAddGraphic,
  onEdit,
}: PlaylistActionBarProps) {
  const dispatch = useDispatch();
  const {
    mosId,
    connected,
    autoFetch,
    statusLoading,
    mosPlaylist,
    handleToggleAutoFetch,
    handlePullMos,
  } = useMosActions(playlistId);

  const rundownId = useSelector((state: StateType) => state.rundown.detail.data?.id);

  const handleDuplicate = useCallback(() => {
    if (!playlistId || !rundownId) return;
    dispatch({
      type: REQUEST_DUPLICATE_PLAYLIST,
      payload: { id: playlistId, rundownId },
    });
  }, [dispatch, playlistId, rundownId]);

  const handleDelete = useCallback(() => {
    if (!playlistId) return;
    dispatch({
      type: REQUEST_DELETE_PLAYLIST,
      payload: { id: playlistId },
    });
  }, [dispatch, playlistId]);

  const disabled = !mosId;

  return (
    <div className="flex gap-3 w-full h-fit items-center justify-between">
      <div
        className="flex items-center gap-1.5 mr-3"
        title={connected ? 'MOS is connected' : 'MOS is not connected'}
      >
        <Icon
          icon={!connected ? 'link-break' : 'link-horizontal'}
          weight="bold"
          className={connected ? 'text-emerald-400' : 'text-rose-400'}
        />
        <Text
          className={classNames(
            'cursor-default',
            connected ? '!text-emerald-400' : '!text-rose-400'
          )}
        >
          MOS
        </Text>
      </div>
      <div className="flex gap-1">
        <Button
          iconProps={{
            icon: autoFetch ? 'link-horizontal-break' : 'link-horizontal',
            weight: 'bold',
            ...(connected && autoFetch && { color: '#4155ff' }),
          }}
          disabled={disabled || statusLoading || !connected}
          onClick={handleToggleAutoFetch}
          title={autoFetch ? 'Disconnect' : 'Connect'}
        />
        <Button
          iconProps={{ icon: 'file-arrow-down', weight: 'bold' }}
          disabled={disabled || statusLoading || !mosPlaylist || !connected}
          onClick={handlePullMos}
          title="Pull MOS"
        />

        <div className="w-px h-5 bg-[rgba(249,250,251,0.1)] self-center mx-0.5" />
        <Button
          iconProps={{ icon: 'plus', weight: 'bold' }}
          disabled={!editable}
          onClick={onAddGraphic}
          title="Add graphic"
        />
        <Button
          iconProps={{ icon: 'pencil', weight: 'bold' }}
          disabled={!editable || !playlistId}
          onClick={onEdit}
          title="Rename playlist"
        />
        <Button
          iconProps={{ icon: 'copy', weight: 'bold' }}
          disabled={!editable || !playlistId}
          onClick={handleDuplicate}
          title="Duplicate playlist"
        />
        <Button
          iconProps={{ icon: 'trash', weight: 'bold' }}
          disabled={!editable || !playlistId}
          onClick={handleDelete}
          title="Delete playlist"
        />
      </div>
    </div>
  );
}
